import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import {RootState, setUserMetadata} from '@/app/store';
import {HttpMethod} from '@/constants/httpMethod';
import {EReducersPath} from '@/dataSource/utils/ReducerPaths';
import logError from '@/utils/errors/logError';

import {setBearerTokenIfExists, setWafHeaders} from '../headers';
import {reAuth} from '../reAuth';

import {apiBaseUsers} from './constants';
import {
  IUpdateProfileMetadataBody,
  IUpdateSourceOfFundsBody,
  IVerificationUserState,
  SignUpCodeResponse,
} from './entities';

const baseQuery = async (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    timeout: 30 * 1000,
    prepareHeaders: async (headers, {getState}) => {
      const token = (getState() as RootState).tokenDataSlice?.tokenData
        ?.accessToken;

      setBearerTokenIfExists(headers, token);
      await setWafHeaders(baseUrl, headers);

      return headers;
    },
  });

const baseApiQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const baseUrl = apiBaseUsers.baseUrl;

  const result = await reAuth(args, api, extraOptions, baseUrl, baseQuery);
  return result;
};

export const usersApi = createApi({
  reducerPath: EReducersPath.USERS_API,
  refetchOnReconnect: true,
  baseQuery: baseApiQueryWithReAuth,
  endpoints: build => ({
    getProfile: build.query<IVerificationUserState, void>({
      query: () => apiBaseUsers.endpoints.profile,
    }),
    verifySignUpCode: build.query<SignUpCodeResponse, string>({
      query: code => `${apiBaseUsers.endpoints.verifySignUpCode}/${code}`,
    }),
    updateProfileMetadata: build.mutation<void, IUpdateProfileMetadataBody>({
      query: body => ({
        url: apiBaseUsers.endpoints.updateProfileMetadata,
        method: HttpMethod.PUT,
        body,
      }),
      async onQueryStarted(body, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled;
          Object.entries(body.metadata).forEach(([key, value]) => {
            dispatch(
              setUserMetadata({
                key,
                value,
              }),
            );
          });
        } catch (error) {
          logError(error, {
            customTitle: 'Error updating profile metadata api',
            fileName: 'users - api.ts',
            functionName: 'updateProfileMetadata',
          });
          throw error;
        }
      },
    }),
    updateSourceOfFunds: build.mutation<void, IUpdateSourceOfFundsBody>({
      query: body => ({
        url: apiBaseUsers.endpoints.sourceOfFunds,
        method: HttpMethod.PUT,
        body,
      }),
    }),
  }),
});

export const {
  useGetProfileQuery,
  useLazyVerifySignUpCodeQuery,
  useLazyGetProfileQuery,
  useUpdateProfileMetadataMutation,
  useUpdateSourceOfFundsMutation,
} = usersApi;
